import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// ─── Constants ─────────────────────────────────────────────
import { organizationsList } from "./constants";
import { ROUTES } from "../../setup/constants";

// ─── Styles ────────────────────────────────────────────────
import styles from "./NewOrganizations.module.scss";

// ─── Components ────────────────────────────────────────────
import Table, { TableColumn } from "../contract/list_v2/Table";
import Search from "../contract/list_v2/Search";
import IconButton from "../contract/list_v2/IconButton";
import Money from "../contract/list_v2/Table/components/Money";
import Pagination from "../../components/pagination";
import Spinner from "../../components/common/Spinner";
import AddOrganisationModal from "../../components/organisations/AddOrganisation";
import DeleteModal from "../../components/organisations/DeleteModal";
import OrganizationRequestDetailsModal from "../../components/organisations/OrganizationRequestDetailsModal";

// ─── Store ─────────────────────────────────────────────────
import { useOrganisationStore } from "../../providers/organisation/organisation.provider";

// ─── Types ─────────────────────────────────────────────────
import {
  IOrganizations,
  TOrganizationVerificationStatus,
} from "../../setup/types/Organisation";

// ─── Utils ─────────────────────────────────────────────────
import {
  ORGANIZATION_REQUEST_STATUS_LABELS,
  canReviewOrganizationRequest,
  getOrganizationRequestStatus,
} from "../../setup/util/organizationRequest";
import PrimaryButton, {
  PrimaryButtonSize,
} from "../../components/common/PrimaryButton";

interface OrgTableData extends IOrganizations {
  joinDate: string;
  email: string;
  phone: string;
  totalTransactions: number;
  currentTransactions: number;
  totalValue: number;
  availableBalance: number;
  // Carried on the row (rather than looked up inside the column renderers) because
  // `visibleColumnsForTab` memoizes the column list and would otherwise close over
  // a stale documents map.
  hasDocuments: boolean;
}

// Tab 1 ("Requests") shows self-registered orgs not yet acted on; these columns
// don't apply to an org that has no live portal client/contracts yet.
const REQUESTS_TAB_ID = 1;
const REQUESTS_TAB_HIDDEN_COLUMNS = [
  "totalTransactions",
  "currentTransactions",
  "totalValue",
  "availableBalance",
];

const Organizations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ─── Zustand Store Selectors ─────────────────────────────
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );
  const organizations = useOrganisationStore((state) => state.organizations);
  const organizationsHaveDocuments = useOrganisationStore(
    (state) => state.organizationsHaveDocuments
  );
  const page = useOrganisationStore((state) => state.page);
  const pageCount = useOrganisationStore((state) => state.pageCount);
  const loading = useOrganisationStore((state) => state.loading);
  const buttonLoading = useOrganisationStore((state) => state.buttonLoading);
  const setPage = useOrganisationStore((state) => state.setPage);
  const searchOrganisation = useOrganisationStore(
    (state) => state.searchOrganisation
  );
  const setVerificationStatusFilter = useOrganisationStore(
    (state) => state.setVerificationStatusFilter
  );

  // ─── Local states ─────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showADDORGANIZATIONModal, setShowADDORGANIZATIONModal] =
    useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchBy, setSearchBy] = useState("name");
  const [detailsModalOrg, setDetailsModalOrg] = useState<
    OrgTableData | undefined
  >(undefined);
  // Row-level "رفض" opens the details modal straight into the reject-reason
  // step; row click / org name ("view details") opens it on the normal step.
  const [openModalInRejectMode, setOpenModalInRejectMode] = useState(false);
  // Row-level "قبول" opens the same details modal and has it immediately run
  // the accept flow, so the modal's own success step (fee template setup)
  // still appears instead of only updating the row in place.
  const [autoAcceptOnOpen, setAutoAcceptOnOpen] = useState(false);

  // Since we don't have settings but the user wants the same components:
  const initialColumnVisibility = {
    company: true,
    code: true,
    joinDate: true,
    email: true,
    phone: true,
    totalTransactions: true,
    currentTransactions: true,
    totalValue: true,
    availableBalance: true,
    verificationStatus: true,
    actions: true,
  };
  const [columnVisibility, setColumnVisibility] = useState(
    initialColumnVisibility
  );

  // ─── Tabs List ─────────────────────────────────────────────
  // Tab 0 shows verified/current organizations; tab 1 ("Requests") shows
  // self-registered organizations — both still PENDING admin approval and
  // already REJECTED ones (shown read-only, as a historical record).
  const list: {
    id: number;
    title: string;
    verificationStatus:
      | TOrganizationVerificationStatus
      | TOrganizationVerificationStatus[];
  }[] = useMemo(
    () => [
      {
        id: 0,
        title: t("lbl_current_organization_list_title"),
        verificationStatus: "VERIFIED",
      },
      {
        id: REQUESTS_TAB_ID,
        title: "الطلبات",
        verificationStatus: ["PENDING", "REJECTED"],
      },
    ],
    [t]
  );

  // ─── Search Options ────────────────────────────────────────
  const searchOptions = [
    { value: "name", label: t("organisation_name") },
    { value: "code", label: t("code") },
  ];

  // ─── Table Data Mapping ──────────────────────────────────
  const tableData = useMemo(() => {
    return (organizations || []).map((org) => ({
      ...org,
      joinDate: org.organizationAdmin?.registrationDate
        ? new Date(org.organizationAdmin.registrationDate).toLocaleDateString(
            "ar-EG",
            { day: "numeric", month: "long", year: "numeric" }
          )
        : "-",
      email: org.organizationAdmin?.email || "-",
      phone: org.organizationAdmin?.phoneNumber || "-",
      totalTransactions: org.contractStats?.totalContractsCount || 0,
      currentTransactions: org.contractStats?.activeContractsCount || 0,
      totalValue: org.contractStats?.totalContractsItemPrice || 0,
      availableBalance: org.contractStats?.availableBalance || 0,
      hasDocuments: Boolean(organizationsHaveDocuments[org.code]),
    }));
  }, [organizations, organizationsHaveDocuments]);

  // ─── Table Columns ────────────────────────────────────────
  const columns: TableColumn<OrgTableData>[] = [
    {
      key: "company",
      label: "الشركة",
      render: (value, row) => (
        <div className={styles.logoCell}>
          {row.logoUrl && <img src={row.logoUrl} alt={row.name} />}
          <span className={styles.companyName}>{row.name}</span>
        </div>
      ),
    },
    {
      key: "code",
      label: "الكود",
      render: (value) => (
        <span className={styles.codeCell} dir="ltr">{`#${value}`}</span>
      ),
    },
    {
      key: "joinDate",
      label: "تاريخ الانضمام",
      render: (value) => <span>{value}</span>,
    },
    {
      key: "email",
      label: "البريد الإلكتروني",
      render: (value) => <span>{value}</span>,
    },
    {
      key: "phone",
      label: "رقم الجوال",
      render: (value) => (
        <span className={styles.ltrCell} dir="ltr">
          {value}
        </span>
      ),
    },
    {
      key: "totalTransactions",
      label: "إجمالي المعاملات",
      render: (value) => <span>{value}</span>,
    },
    {
      key: "currentTransactions",
      label: "المعاملات الجارية",
      render: (value) => <span>{value}</span>,
    },
    {
      key: "totalValue",
      label: "إجمالي قيمة المعاملات",
      render: (value) => <Money amount={value} />,
    },
    {
      key: "availableBalance",
      label: "الرصيد المتاح",
      render: (value) => <Money amount={value} />,
    },
    {
      key: "verificationStatus",
      label: "الحالة",
      render: (value, row) => {
        const status = getOrganizationRequestStatus(row, row.hasDocuments);
        return (
          <span
            className={classNames(styles.statusPill, {
              [styles.active]: status === "VERIFIED",
              [styles.awaiting]: status === "AWAITING_DOCUMENTS",
              [styles.inactive]:
                status === "PENDING" || status === "REJECTED",
            })}>
            {ORGANIZATION_REQUEST_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: t("procedures"),
      width: "220px",
      render: (value, row) => {
        // PENDING/REJECTED rows (Requests tab): quick actions stay in the row
        // (Accept is one click; Reject/view-reason open the same details
        // modal the row click opens, since rejecting needs a reason).
        if (row.verificationStatus === "PENDING") {
          // Nothing to review until the organization uploads its documents, so both
          // actions are inert until then — same rule the details modal enforces.
          const canReview = canReviewOrganizationRequest(row, row.hasDocuments);
          if (!canReview) {
            return (
              <div className={styles.actionCell}>
                {["قبول", "رفض"].map((label) => (
                  <span
                    key={label}
                    className={classNames(
                      styles.actionText,
                      styles.disabledText
                    )}
                    // Swallowed so a disabled action is genuinely inert instead of
                    // bubbling up to the row click and opening the details modal.
                    onClick={(e) => e.stopPropagation()}
                    title="لا يمكن اتخاذ إجراء قبل رفع المؤسسة للمستندات المطلوبة">
                    {label}
                  </span>
                ))}
              </div>
            );
          }
          return (
            <div className={styles.actionCell}>
              <span
                className={classNames(styles.actionText, styles.editText)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!buttonLoading) {
                    setOpenModalInRejectMode(false);
                    setAutoAcceptOnOpen(true);
                    setDetailsModalOrg(row);
                  }
                }}>
                قبول
              </span>
              <span
                className={classNames(styles.actionText, styles.deleteText)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenModalInRejectMode(true);
                  setAutoAcceptOnOpen(false);
                  setDetailsModalOrg(row);
                }}>
                رفض
              </span>
            </div>
          );
        }
        if (row.verificationStatus === "REJECTED") {
          return (
            <div className={styles.actionCell}>
              <span
                className={classNames(styles.actionText, styles.editText)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenModalInRejectMode(false);
                  setAutoAcceptOnOpen(false);
                  setDetailsModalOrg(row);
                }}>
                عرض سبب الرفض
              </span>
            </div>
          );
        }
        return (
          <div className={styles.actionCell}>
            <span
              className={classNames(styles.actionText, styles.editText)}
              onClick={(e) => {
                e.stopPropagation();
                setShowADDORGANIZATIONModal(true);
                setSelectedOrganization(row);
              }}>
              {t("btn_edit_per_row")}
            </span>
            <span
              className={classNames(styles.actionText, styles.deleteText)}
              onClick={(e) => {
                e.stopPropagation();
                toggleDeleteModal(row);
              }}>
              {t("btn_delete_per_row")}
            </span>
          </div>
        );
      },
    },
  ];

  // Columns not relevant to the Requests tab (PENDING/REJECTED orgs have no live
  // portal client/contracts yet) — hidden there regardless of the per-column toggle.
  const visibleColumnsForTab = useMemo(
    () =>
      selectedIndex === REQUESTS_TAB_ID
        ? columns.filter((col) => !REQUESTS_TAB_HIDDEN_COLUMNS.includes(col.key))
        : columns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIndex]
  );

  // ─── Handlers ────────────────────────────────────────────
  const toggleCreateNewOrganisation = useCallback(() => {
    setShowADDORGANIZATIONModal((prev) => !prev);
  }, []);

  useEffect(() => {
    const activeTab = list.find((item) => item.id === selectedIndex);
    setVerificationStatusFilter(activeTab?.verificationStatus);
  }, [selectedIndex, list, setVerificationStatusFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      searchOrganisation(searchValue);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [page, searchValue, selectedIndex, searchOrganisation]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  const toggleDeleteModal = useCallback(
    (org?: IOrganizations) => {
      if (org && !showDeleteModal) setSelectedOrganization(org);
      else setSelectedOrganization(undefined);
      setShowDeleteModal((prev) => !prev);
    },
    [setSelectedOrganization, showDeleteModal]
  );

  const handleMenuChange = (value: string, checked: boolean) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [value]: checked,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  // ─── Render ──────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t("organisation_title")}</h1>

      <div className={styles.tabContainer}>
        {list?.map((listItem) => (
          <li
            key={listItem?.title}
            onClick={() => {
              setSelectedIndex(listItem?.id);
              setPage(1);
            }}
            className={classNames(styles.listItem, {
              [styles.listItemSelected]: listItem?.id === selectedIndex,
              [styles.firstItem]: listItem?.id === 0,
              [styles.lastItem]: listItem?.id === list.length - 1,
            })}>
            {listItem?.title}
          </li>
        ))}
      </div>

      <div className={styles.filtersContainer}>
        <div className={styles.rightSection}>
          <IconButton
            mode="menu"
            icon="/assets/images/svg/settings.svg"
            mainOptions={visibleColumnsForTab.map((col) => ({
              value: col.key,
              label: col.label,
            }))}
            operationOptions={[]}
            selectedValues={columnVisibility}
            onChange={handleMenuChange}
            onSelectAllMain={(checked) => {
              const updated = { ...columnVisibility };
              visibleColumnsForTab.forEach((col) => {
                updated[col.key] = checked;
              });
              setColumnVisibility(updated);
            }}
            onSelectAllOperations={() => {
              /* No operations */
            }}
          />
          <Search
            value={searchValue}
            onChange={handleSearchChange}
            searchBy={searchBy}
            onSearchByChange={setSearchBy}
            searchOptions={searchOptions}
            className={styles.searchInputCustom}
            placeholder={organizationsList.FIND_A_ORGANIZATION}
          />
        </div>

        <div className={styles.leftSection}>
          <PrimaryButton
            size={PrimaryButtonSize.MEDIUM}
            style={{
              width: "184px",
              direction: "ltr",
              fontSize: "14px",
              fontWeight: 500,
            }}
            onClick={toggleCreateNewOrganisation}>
            إضافة شركة +
          </PrimaryButton>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            width: "100%",
            height: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Spinner />
        </div>
      ) : selectedIndex === REQUESTS_TAB_ID && tableData.length === 0 ? (
        <div className={styles.emptyState}>لا توجد طلبات جديدة</div>
      ) : (
        <>
          <Table
            columns={visibleColumnsForTab}
            columnVisibility={columnVisibility}
            data={tableData}
            onRowClick={
              selectedIndex === REQUESTS_TAB_ID
                ? (row) => {
                    setOpenModalInRejectMode(false);
                    setAutoAcceptOnOpen(false);
                    setDetailsModalOrg(row);
                  }
                : (row) =>
                    navigate(
                      ROUTES.ORGANIZATIONS_DETAILS?.replace(":code", row.code)
                    )
            }
          />
          <div className={styles.paginationContainer}>
            <Pagination
              countName={t("organisation_list")}
              pageSize={10}
              pagesCount={pageCount}
              page={page}
              length={organizations?.length || 0}
              handlePaginationChange={handlePaginationChange}
            />
          </div>
        </>
      )}

      {/* ─── Modals ───────────────────────────────────── */}
      <AddOrganisationModal
        showModal={showADDORGANIZATIONModal}
        closeModal={() => {
          setSelectedOrganization(undefined);
          setShowADDORGANIZATIONModal(false);
        }}
      />

      <DeleteModal showModal={showDeleteModal} closeModal={toggleDeleteModal} />

      <OrganizationRequestDetailsModal
        showModal={detailsModalOrg !== undefined}
        closeModal={() => {
          setDetailsModalOrg(undefined);
          setOpenModalInRejectMode(false);
          setAutoAcceptOnOpen(false);
        }}
        organization={detailsModalOrg}
        initialShowRejectInput={openModalInRejectMode}
        autoAccept={autoAcceptOnOpen}
      />
    </div>
  );
};

export default Organizations;
